import { useMemo, useState } from "react";
import { Heart, MapPin, Star } from "lucide-react";
import { MadeWithViny } from "../MadeWithViny";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import { AlbumDetailDrawer } from "../AlbumDetailDrawer";
import type { AlbumItem, TemplateProps } from "../types";
import { cn } from "@/lib/utils";

/* ─────────────────────────────────────────────────────────────
   Diário — template gratuito inspirado em diários de coleção.
   Header com avatar + stats, barra de abas e conteúdo:
   Coleção → Interesse → Avaliações → Atividade → Curtidas.
   ───────────────────────────────────────────────────────────── */

function Stars({ value, size = 12 }: { value: number; size?: number }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-[1px]" aria-label={`${value} de 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        const state = i < full ? "full" : i === full && half ? "half" : "empty";
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star className="absolute inset-0 text-white/15" style={{ width: size, height: size }} />
            {state !== "empty" && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: state === "half" ? size / 2 : size }}
              >
                <Star
                  className="text-[#9231FF]"
                  fill="currentColor"
                  style={{ width: size, height: size }}
                />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

function CoverCard({
  album,
  onSelect,
  showRating = true,
}: {
  album: AlbumItem;
  onSelect: (a: AlbumItem) => void;
  showRating?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(album)}
      className="group min-w-0 text-left"
      title={`${album.title} — ${album.artist}`}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-md bg-white/[0.06] ring-1 ring-white/10 transition duration-200 group-hover:-translate-y-0.5 group-hover:ring-[#9231FF]/60">
        {album.cover_url ? (
          <img
            src={album.cover_url}
            alt={album.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="grid h-full place-items-center px-2 text-center text-[10px] text-white/40">
            {album.title}
          </span>
        )}
      </div>
      <div className="mt-1.5 min-w-0">
        <div className="truncate text-[12px] font-medium leading-tight text-white/90">
          {album.title}
        </div>
        <div className="truncate text-[11px] leading-tight text-white/45">{album.artist}</div>
        {showRating && (
          <div className="mt-1 flex items-center gap-1.5">
            {album.rating ? (
              <>
                <Stars value={album.rating} />
                <span className="text-[10px] tabular-nums text-white/40">
                  {album.rating.toFixed(1)}
                </span>
              </>
            ) : (
              <span className="text-[10px] text-white/25">sem nota</span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

type TabKey = "collection" | "wishlist" | "rated" | "activity" | "likes";

const ACTIVITY_LABEL: Record<string, string> = {
  added_to_collection: "adicionou à coleção",
  added_to_wishlist: "adicionou aos interesses",
  rated_album: "avaliou as faixas de",
  rated_tracks: "avaliou as faixas de",
  for_sale: "colocou à venda",
  for_trade: "colocou para troca",
};

function relTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86_400_000);
  if (d <= 0) return "hoje";
  if (d === 1) return "ontem";
  if (d < 30) return `${d} dias atrás`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m} ${m === 1 ? "mês" : "meses"} atrás`;
  const y = Math.floor(m / 12);
  return `${y} ${y === 1 ? "ano" : "anos"} atrás`;
}

export function DiarioTemplate({ bio, config }: TemplateProps) {
  const [selected, setSelected] = useState<AlbumItem | null>(null);
  const [tab, setTab] = useState<TabKey>("collection");

  const name = config.title || bio.display_name || bio.username;
  const tagline = config.subtitle || bio.bio;
  const place = [bio.city, bio.country].filter(Boolean).join(", ");
  const collection = config.sections.collection !== false ? bio.owned : [];
  const wishlist = config.sections.wishlist !== false ? bio.wishlist : [];
  const activity = bio.activity ?? [];
  const likes = bio.likes ?? [];

  const rated = useMemo(
    () =>
      collection
        .filter((a) => typeof a.rating === "number" && a.rating! > 0)
        .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
    [collection],
  );
  const average = useMemo(
    () => (rated.length ? rated.reduce((s, a) => s + (a.rating ?? 0), 0) / rated.length : 0),
    [rated],
  );

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: "collection", label: "Coleção", count: collection.length },
    { key: "wishlist", label: "Interesse", count: wishlist.length },
    { key: "rated", label: "Avaliações", count: rated.length },
    { key: "activity", label: "Atividade" },
    { key: "likes", label: "Curtidas", count: likes.length },
  ];

  const gridItems =
    tab === "collection" ? collection : tab === "wishlist" ? wishlist : tab === "rated" ? rated : [];

  const stats = [
    { v: String(collection.length), l: "Discos" },
    { v: String(rated.length), l: "Avaliados" },
    { v: average ? average.toFixed(1) : "—", l: "Nota média" },
    { v: String(bio.counts.followers), l: "Seguidores" },
  ];

  return (
    <div className="min-h-screen bg-[#14181c] text-white">
      {/* HEADER */}
      <header className="border-b border-white/10 bg-[#111418]">
        <div className="mx-auto w-full max-w-[1200px] px-4 pb-0 pt-8 sm:px-6 lg:pt-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4 sm:gap-5">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/15 sm:h-24 sm:w-24">
                {bio.avatar_url ? (
                  <img src={bio.avatar_url} alt={name} className="h-full w-full object-cover" />
                ) : (
                  <span className="grid h-full place-items-center text-xl text-white/40">
                    {name.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="flex items-center gap-2 text-[22px] font-semibold leading-tight tracking-tight sm:text-3xl">
                  <span className="min-w-0 break-words">{name}</span>
                  {bio.is_premium && (
                    <VerifiedBadge variant="shimmer" size={18} aria-label="Verificado" />
                  )}
                </h1>
                <div className="mt-1 text-[12px] text-white/45">@{bio.username}</div>
                {tagline && (
                  <p className="mt-2.5 max-w-xl text-[13.5px] leading-relaxed text-white/65">
                    {tagline}
                  </p>
                )}
                {place && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-white/45">
                    <MapPin className="h-3.5 w-3.5" /> {place}
                  </div>
                )}
                {bio.favorite_genres.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {bio.favorite_genres.slice(0, 6).map((g) => (
                      <span
                        key={g}
                        className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-white/60"
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* STATS estilo diário */}
            <div className="grid grid-cols-4 divide-x divide-white/10 rounded-xl border border-white/10 bg-white/[0.03] lg:border-0 lg:bg-transparent">
              {stats.map((s) => (
                <div key={s.l} className="min-w-0 px-1.5 py-3 text-center sm:px-3 lg:px-5">
                  <div className="text-lg font-bold tabular-nums leading-none sm:text-2xl">
                    {s.v}
                  </div>
                  <div className="mt-1.5 text-[8px] uppercase leading-tight tracking-[0.08em] text-white/40 sm:text-[10px] sm:tracking-[0.16em]">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* TABS */}
          <nav
            className="mt-7 -mx-4 flex gap-5 overflow-x-auto px-4 pb-px sm:mx-0 sm:px-0"
            style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "relative shrink-0 whitespace-nowrap pb-3 text-[13.5px] font-medium transition sm:text-[15px]",
                  tab === t.key ? "text-white" : "text-white/50 hover:text-white/80",
                )}
              >
                {t.label}
                {typeof t.count === "number" && (
                  <span className="ml-1.5 text-[11px] tabular-nums text-white/35">{t.count}</span>
                )}
                {tab === t.key && (
                  <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[#9231FF]" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1200px] px-4 pb-16 pt-7 sm:px-6">
        {(tab === "collection" || tab === "wishlist" || tab === "rated") &&
          (gridItems.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed border-white/15 py-20 text-center text-[13px] text-white/40">
              {tab === "rated" ? "Nenhuma nota por aqui ainda." : "Nada por aqui ainda."}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-6">
              {gridItems.slice(0, 240).map((a) => (
                <CoverCard
                  key={`${tab}-${a.id}`}
                  album={a}
                  onSelect={setSelected}
                  showRating={tab !== "wishlist" && rated.length > 0}
                />
              ))}
            </div>
          ))}

        {tab === "activity" && (
          <div className="divide-y divide-white/10 overflow-hidden rounded-xl border border-white/10">
            {activity.length === 0 ? (
              <div className="py-20 text-center text-[13px] text-white/40">
                Nenhuma atividade recente.
              </div>
            ) : (
              activity.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-white/[0.06]">
                    {a.album?.cover_url && (
                      <img
                        src={a.album.cover_url}
                        alt={a.album.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-white/85">
                      <span className="text-white/50">
                        {ACTIVITY_LABEL[a.type] ?? "registrou"}{" "}
                      </span>
                      {a.album?.title ?? "—"}
                    </div>
                    <div className="truncate text-[11px] text-white/40">
                      {a.album?.artist ?? ""}
                    </div>
                  </div>
                  <span className="shrink-0 text-[10.5px] text-white/30">
                    {relTime(a.created_at)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "likes" && (
          <div className="divide-y divide-white/10 overflow-hidden rounded-xl border border-white/10">
            {likes.length === 0 ? (
              <div className="py-20 text-center text-[13px] text-white/40">
                Nenhuma curtida ainda.
              </div>
            ) : (
              likes.map((l) => (
                <div key={l.post_id} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-white/[0.06]">
                    {l.album?.cover_url && (
                      <img
                        src={l.album.cover_url}
                        alt={l.album.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-white/85">{l.album?.title ?? "—"}</div>
                    <div className="truncate text-[11px] text-white/40">
                      {l.album?.artist ?? ""}
                      {l.author_username ? ` · por @${l.author_username}` : ""}
                    </div>
                  </div>
                  <Heart className="h-4 w-4 shrink-0 text-[#9231FF]" fill="currentColor" />
                  <span className="shrink-0 text-[10.5px] text-white/30">
                    {relTime(l.created_at)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-white/10 px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/35">
          <MadeWithViny variant="light" />
          <span className="tabular-nums">{collection.length + wishlist.length} itens</span>
        </div>
      </footer>

      <AlbumDetailDrawer
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </div>
  );
}
